/**
 * Selectable options for the tradie profile's Medical Conditions and PPE Size
 * fields, plus helpers to round-trip them through the backend's plain-string
 * columns.
 *
 * The backend stores `medical_conditions` and `ppe_size` as free-text strings
 * (the mobile app writes comma-separated condition labels), so the web
 * presents pickers but serialises back to the same string format. Existing
 * free-typed values that don't match a known option are preserved via the
 * "Other" field rather than dropped.
 *
 * MEDICAL_CONDITIONS mirrors the mobile app's `assets/medical.json`
 * (tradiepass-flutter) — keep the two in sync. The app flattens the
 * categories, sorts alphabetically and pins "Other" at the top of its picker;
 * MEDICAL_CONDITION_OPTIONS reproduces the flatten+sort (deduped — a few
 * conditions appear under two categories) and the picker component pins
 * "Other".
 */

const MEDICAL_CONDITIONS = [
  // Musculoskeletal / Mobility
  'Back problems',
  'Arthritis',
  'Sciatica',
  'Chronic pain',
  'Frozen shoulder',
  'Tennis elbow',
  'Golfers elbow',
  'Shoulder pain (rotator cuff)',
  'Subacromial pain syndrome',
  'Ankle sprain',
  'Ankle avulsion fracture',
  'Clavicle fracture',
  'Wrist fracture',
  'Thumb fracture',
  'Metacarpal fracture',
  'Joint injury',
  'Osteoarthritis (hip)',
  'Osteoarthritis (knee)',
  'Osteoarthritis (hand)',
  'Osteoporosis',
  'Gout',
  'Bunion',
  // Respiratory
  'Asthma',
  'COPD',
  'Pneumonia',
  'Bronchitis',
  'Bronchiectasis',
  'Chronic respiratory infection (ARI)',
  'Cough',
  'Shortness of breath',
  'Flu',
  'COVID-19',
  'Long COVID',
  // Heart / Circulation
  'Heart attack',
  'Angina',
  'Atrial fibrillation',
  'Supraventricular tachycardia',
  'Hypertension',
  'Heart failure',
  'Cardiovascular disease',
  'Heart block',
  'Stroke (history)',
  'Deep vein thrombosis',
  'Arterial thrombosis',
  'Raynaud’s phenomenon',
  // Neurological
  'Epilepsy',
  'Migraines',
  'Multiple sclerosis',
  'Motor neurone disease',
  'Dystonia',
  'Transient ischaemic attack (TIA)',
  'Vertigo',
  'Severe dizziness',
  'Functional neurological disorder',
  'Ataxia',
  // Infectious (Flu, COVID-19, Pneumonia, Bronchitis also listed above)
  'Gastroenteritis',
  'Norovirus',
  'Hand, foot and mouth disease',
  'Conjunctivitis',
  'Cellulitis',
  'Impetigo',
  'Strep A',
  'RSV',
  'Chickenpox',
  'Shingles',
  // Allergies / Skin
  'Severe allergies',
  'Anaphylaxis',
  'Eczema (hands)',
  'Contact dermatitis',
  'Psoriasis',
  'Hives / urticaria',
  'Fungal skin infections',
  'Sunburn',
  // Mental Health
  'Severe anxiety',
  'Depression (severe)',
  'PTSD',
  'Psychosis',
  'Bipolar disorder',
  'OCD',
  // Metabolic / Acute
  'Diabetes',
  'Diabetic ketoacidosis (DKA)',
  'Hypoglycaemia',
  'Hyperglycaemia',
  'Obesity',
  'Dehydration',
  'Heatstroke',
  'Hypothermia',
  'Photosensitivity',
]

export const MEDICAL_CONDITION_OPTIONS = Array.from(
  new Set(MEDICAL_CONDITIONS),
).sort((a, b) => a.localeCompare(b))

/**
 * Mirror of the app's offline fallback for `GET /ppe-sizes` — used until the
 * endpoint responds (or when it fails). Like the app, the lowercase `value`
 * is what gets submitted as `ppe_size`.
 */
export const PPE_SIZE_FALLBACK_OPTIONS = [
  { value: 'xs', text: 'XS' },
  { value: 's', text: 'S' },
  { value: 'm', text: 'M' },
  { value: 'l', text: 'L' },
  { value: 'xl', text: 'XL' },
  { value: 'xxl', text: 'XXL' },
  { value: 'xxxl', text: 'XXXL' },
]

/**
 * Load PPE size options from the API (same public endpoint the app uses),
 * mapped to b-form-select's { value, text } shape. Falls back to
 * PPE_SIZE_FALLBACK_OPTIONS on any error or empty response.
 *
 * @param {{ get: Function }} api axios-like client
 * @returns {Promise<Array<{ value: string, text: string }>>}
 */
export async function fetchPpeSizeOptions(api) {
  try {
    const { data } = await api.get('ppe-sizes')
    const raw = data && data.data
    if (Array.isArray(raw)) {
      const options = raw
        .filter(
          (item) =>
            item && item.value != null && String(item.value).trim() !== '',
        )
        .map((item) => ({
          value: String(item.value),
          text:
            item.label != null && String(item.label) !== ''
              ? String(item.label)
              : String(item.value).toUpperCase(),
        }))
      if (options.length) return options
    }
  } catch (e) {
    // fall through to the offline defaults
  }
  return PPE_SIZE_FALLBACK_OPTIONS.slice()
}

/**
 * Split a stored medical-conditions string into known selections and leftover
 * free text. Matching is case-insensitive. Labels that themselves contain a
 * comma (e.g. "Hand, foot and mouth disease") are extracted before the string
 * is split. Unmatched parts are joined into `other` so legacy free-typed
 * values survive the round trip.
 *
 * @param {string|null} value stored string, e.g. "Asthma, Diabetes, hay fever"
 * @returns {{ selected: string[], other: string|null }}
 */
export function parseMedicalConditions(value) {
  const result = { selected: [], other: null }
  if (!value || typeof value !== 'string') return result

  // Pull out multi-part labels first so the comma split can't break them.
  let remaining = value
  MEDICAL_CONDITION_OPTIONS.filter((option) => option.includes(',')).forEach(
    (option) => {
      const index = remaining.toLowerCase().indexOf(option.toLowerCase())
      if (index !== -1) {
        result.selected.push(option)
        remaining =
          remaining.slice(0, index) + remaining.slice(index + option.length)
      }
    },
  )

  const byLowerCase = {}
  MEDICAL_CONDITION_OPTIONS.forEach((option) => {
    byLowerCase[option.toLowerCase()] = option
  })

  const leftovers = []
  remaining
    .split(',')
    .map((part) => part.trim())
    .filter((part) => part !== '')
    .forEach((part) => {
      const match = byLowerCase[part.toLowerCase()]
      if (match && !result.selected.includes(match)) {
        result.selected.push(match)
      } else if (!match) {
        leftovers.push(part)
      }
    })

  result.other = leftovers.length ? leftovers.join(', ') : null
  return result
}

/**
 * Serialise picker state back to the backend's plain-string format. Matches
 * the app: the "Other" free text is appended in place of the Other option.
 *
 * @param {string[]} selected chosen options
 * @param {string|null} other free-text extra conditions
 * @returns {string|null} e.g. "Asthma, Diabetes, hay fever", or null when empty
 */
export function serializeMedicalConditions(selected, other) {
  const parts = (Array.isArray(selected) ? selected : []).slice()
  const extra = other && String(other).trim()
  if (extra) parts.push(extra)
  return parts.length ? parts.join(', ') : null
}
